import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../Context';
import { AuthContextApi } from '../../../Context/api';
import NavMenu from '../../../Components/NavMenu/Tecnico';
import {
    DocumentTextIcon,
    ChartBarIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XCircleIcon,
    EyeIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    GlobeAltIcon,
    CodeBracketIcon,
    CalendarIcon,
    UserIcon
} from '@heroicons/react/24/outline';

const Requests = () => {
    const { states, setters } = useContext(AuthContext);
    const {
        checkLogin,
        getRequestsDataSistema,
        getRequestsMetricas,
        getRequests
    } = useContext(AuthContextApi);

    // Estados para ordenação e paginação
    const [selectedSortField, setSelectedSortField] = useState('data_criacao');
    const [sortDirection, setSortDirection] = useState('desc');
    const [recordsPerPage, setRecordsPerPage] = useState(10);

    // Estados para modal de visualização
    const [viewingRequest, setViewingRequest] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    // Opções de ordenação
    const sortOptions = [
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'method', label: 'Método HTTP' },
        { value: 'endpoint', label: 'Endpoint' },
        { value: 'status_code', label: 'Status Code' },
        { value: 'duration_ms', label: 'Duração' },
        { value: 'user_email', label: 'Usuário' }
    ];

    const primeiroLoad = async () => {
        await getRequestsMetricas();
        await getRequests('page=1&per_page=10&sort=data_criacao&sort_dir=desc');
        await checkLogin();
    };

    const loadRequests = async () => {
        const params = new URLSearchParams({
            page: states.page.toString(),
            per_page: recordsPerPage.toString(),
            sort: selectedSortField,
            sort_dir: sortDirection
        });

        console.log('loadRequests - params:', params.toString());
        console.log('loadRequests - states.dataRequests antes:', states.dataRequests);
        await getRequests(params.toString());
        console.log('loadRequests - states.dataRequests depois:', states.dataRequests);
    };

    const next = () => {
        setters.setPage(states.page + 1);
    };

    const previous = () => {
        if (states.page > 1) {
            setters.setPage(states.page - 1);
        }
    };

    const openViewModal = (request) => {
        setViewingRequest(request);
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setViewingRequest(null);
        setShowViewModal(false);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('pt-BR');
    };

    const formatDuration = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const getStatusColor = (statusCode) => {
        if (statusCode >= 200 && statusCode < 300) return 'text-green-600 bg-green-100';
        if (statusCode >= 400 && statusCode < 500) return 'text-yellow-600 bg-yellow-100';
        if (statusCode >= 500) return 'text-red-600 bg-red-100';
        return 'text-gray-600 bg-gray-100';
    };

    const getMethodColor = (method) => {
        switch (method) {
            case 'GET': return 'text-brand-cyan-dark bg-brand-cyan/20';
            case 'POST': return 'text-green-600 bg-green-100';
            case 'PUT': return 'text-yellow-600 bg-yellow-100';
            case 'PATCH': return 'text-brand-cyan-dark bg-brand-cyan/20';
            case 'DELETE': return 'text-red-600 bg-red-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    useEffect(() => {
        primeiroLoad();
        return () => {
            setters.setDataEstatisticaRequests([]);
            setters.setDataRequests([]);
            setters.setPage(1);
        };
    }, []);

    useEffect(() => {
        loadRequests();
    }, [states.page, sortDirection, selectedSortField, recordsPerPage]);

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
                <NavMenu />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-indigo-100 to-blue-100">
                                <DocumentTextIcon className="w-8 h-8 text-brand-cyan-dark" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-indigo-800 to-blue-800 bg-clip-text text-transparent">
                                    Requests do Sistema
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    Monitoramento e análise de requisições da API
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Métricas */}
                    {states.dataEstatisticaRequests?.general && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            <div className="relative bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50"></div>
                                <div className="relative p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-2xl bg-brand-cyan/20">
                                            <ChartBarIcon className="w-6 h-6 text-brand-cyan-dark" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Total de Requests</p>
                                            <p className="text-2xl font-bold text-gray-900">
                                                {states.dataEstatisticaRequests.general.total_requests || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="relative bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-emerald-50/50"></div>
                                <div className="relative p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-2xl bg-green-100">
                                            <CheckCircleIcon className="w-6 h-6 text-green-600" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Sucessos</p>
                                            <p className="text-2xl font-bold text-gray-900">
                                                {states.dataEstatisticaRequests.general.success_requests || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="relative bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-yellow-50/50 to-orange-50/50"></div>
                                <div className="relative p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-2xl bg-yellow-100">
                                            <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Erros Cliente</p>
                                            <p className="text-2xl font-bold text-gray-900">
                                                {states.dataEstatisticaRequests.general.client_errors || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="relative bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-red-50/50 to-pink-50/50"></div>
                                <div className="relative p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-2xl bg-red-100">
                                            <XCircleIcon className="w-6 h-6 text-red-600" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Erros Servidor</p>
                                            <p className="text-2xl font-bold text-gray-900">
                                                {states.dataEstatisticaRequests.general.server_errors || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Controles de Ordenação */}
                    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 mb-8">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Ordenar por
                                </label>
                                <select
                                    value={selectedSortField}
                                    onChange={(e) => setSelectedSortField(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                >
                                    {sortOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Direção
                                </label>
                                <select
                                    value={sortDirection}
                                    onChange={(e) => setSortDirection(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                >
                                    <option value="asc">Crescente</option>
                                    <option value="desc">Decrescente</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Registros por página
                                </label>
                                <select
                                    value={recordsPerPage}
                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>

                            <div className="flex items-end">
                                <button
                                    onClick={loadRequests}
                                    className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2"
                                >
                                    <MagnifyingGlassIcon className="w-5 h-5" />
                                    <span>Atualizar</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Lista de Requests */}
                    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Histórico de Requests
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Lista detalhada de todas as requisições do sistema
                            </p>
                        </div>

                        <div className="divide-y divide-gray-100">
                            {states.dataRequests?.map((request) => (
                                <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-4 mb-3">
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getMethodColor(request.method)}`}>
                                                    {request.method}
                                                </span>
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status_code)}`}>
                                                    {request.status_code}
                                                </span>
                                                <span className="text-sm text-gray-500">
                                                    {formatDuration(request.duration_ms)}
                                                </span>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center space-x-2">
                                                    <CodeBracketIcon className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 truncate">
                                                        {request.endpoint}
                                                    </span>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <UserIcon className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600 truncate">
                                                        {request.user_email}
                                                    </span>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <GlobeAltIcon className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600">
                                                        IP: {request.ip} (Rede)
                                                    </span>
                                                </div>

                                                <div className="flex items-center space-x-2">
                                                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600">
                                                        {formatDate(request.data_criacao)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="ml-4">
                                            <button
                                                onClick={() => openViewModal(request)}
                                                className="p-3 rounded-xl bg-brand-cyan/20 text-brand-cyan-dark hover:bg-brand-cyan/30 transition-colors"
                                            >
                                                <EyeIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Paginação */}
                        <div className="px-6 py-4 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Página {states.page} • {recordsPerPage} registros por página
                                </div>

                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={previous}
                                        disabled={states.page <= 1}
                                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeftIcon className="w-4 h-4 mr-1" />
                                        Anterior
                                    </button>

                                    <button
                                        onClick={next}
                                        disabled={states.dataRequests?.length < recordsPerPage}
                                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Próximo
                                        <ChevronRightIcon className="w-4 h-4 ml-1" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de Visualização */}
            {showViewModal && viewingRequest && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={closeViewModal}></div>

                        <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 rounded-xl bg-brand-cyan/20">
                                        <DocumentTextIcon className="w-6 h-6 text-brand-cyan-dark" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        Detalhes da Request
                                    </h3>
                                </div>
                                <button
                                    onClick={closeViewModal}
                                    className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Informações Básicas */}
                                    <div className="space-y-4">
                                        <div className="bg-gray-50 rounded-2xl p-4">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Informações Básicas</h4>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">ID</label>
                                                    <p className="text-sm text-gray-900 font-mono">{viewingRequest.id}</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Método HTTP</label>
                                                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getMethodColor(viewingRequest.method)}`}>
                                                        {viewingRequest.method}
                                                    </span>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Endpoint</label>
                                                    <p className="text-sm text-gray-900 font-mono break-all">{viewingRequest.endpoint}</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Status Code</label>
                                                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(viewingRequest.status_code)}`}>
                                                        {viewingRequest.status_code}
                                                    </span>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Duração</label>
                                                    <p className="text-sm text-gray-900">{formatDuration(viewingRequest.duration_ms)}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Usuário e IP */}
                                        <div className="bg-gray-50 rounded-2xl p-4">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Origem</h4>
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Usuário</label>
                                                    <p className="text-sm text-gray-900">{viewingRequest.user_email}</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Endereço IP</label>
                                                    <p className="text-sm text-gray-900">{viewingRequest.ip} (Rede)</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Data de Criação</label>
                                                    <p className="text-sm text-gray-900">{formatDate(viewingRequest.data_criacao)}</p>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-600">Data de Atualização</label>
                                                    <p className="text-sm text-gray-900">{formatDate(viewingRequest.data_atualizacao)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dados da Request */}
                                    <div className="space-y-4">
                                        {/* Body */}
                                        <div className="bg-gray-50 rounded-2xl p-4">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Body</h4>
                                            <pre className="text-xs text-gray-700 bg-white p-3 rounded-lg overflow-x-auto">
                                                {viewingRequest.body ? JSON.stringify(JSON.parse(viewingRequest.body), null, 2) : 'Nenhum body'}
                                            </pre>
                                        </div>

                                        {/* Query */}
                                        <div className="bg-gray-50 rounded-2xl p-4">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Query Parameters</h4>
                                            <pre className="text-xs text-gray-700 bg-white p-3 rounded-lg overflow-x-auto">
                                                {viewingRequest.query ? JSON.stringify(JSON.parse(viewingRequest.query), null, 2) : 'Nenhum query'}
                                            </pre>
                                        </div>

                                        {/* Headers */}
                                        <div className="bg-gray-50 rounded-2xl p-4">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Headers</h4>
                                            <pre className="text-xs text-gray-700 bg-white p-3 rounded-lg overflow-x-auto">
                                                {viewingRequest.headers ? JSON.stringify(JSON.parse(viewingRequest.headers), null, 2) : 'Nenhum header'}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Requests;